import { useEffect, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';

import Check from '@/icons/check.svg';
import ChevronDown from '@/icons/chevron-down.svg';
import Cross from '@/icons/close.svg';
import { cn } from '@/utils';

import Label from './form/Label';

export function MultiSelect({
	disabledOptions = [],
	options,
	selected,
	onChange,
	placeholder,
	className = '',
	labelText = 'Title',
	required = false,
	searchable = false
}) {
	const { t } = useTranslation();
	const [isOpen, setIsOpen] = useState(false);
	const [searchTerm, setSearchTerm] = useState('');
	const containerRef = useRef(null);
	const searchInputRef = useRef(null);

	const filteredOptions = options.filter(option =>
		option.label.toLowerCase().includes(searchTerm.toLowerCase())
	);

	useEffect(() => {
		function handleClickOutside(event) {
			if (
				containerRef.current &&
				!containerRef.current.contains(event.target)
			) {
				setIsOpen(false);
			}
		}

		document.addEventListener('mousedown', handleClickOutside);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, []);

	useEffect(() => {
		if (isOpen && searchInputRef.current) {
			searchInputRef.current.focus();
		}
	}, [isOpen]);

	const toggleOption = value => {
		if (selected.includes(value)) {
			onChange(selected.filter(item => item !== value));
		} else {
			onChange([...selected, value]);
		}
	};

	const removeOption = (value, e) => {
		e?.stopPropagation();
		onChange(selected.filter(item => item !== value));
	};

	const getSelectedLabel = value => {
		const option = options.find(op => op.value === value);
		return option ? option.label : value;
	};

	const handleKeyDown = e => {
		if (e.key === 'Escape') {
			setIsOpen(false);
		} else if (e.key === 'ArrowDown' && !isOpen) {
			setIsOpen(true);
		}
	};

	return (
		<div
			className={`relative w-full ${className}`}
			ref={containerRef}
			onKeyDown={handleKeyDown}
		>
			{labelText && <Label>{`${labelText}${required ? '*' : ''}`}</Label>}
			<div
				className={cn(
					'flex h-10 w-full cursor-pointer flex-wrap items-center gap-1 rounded-md border border-grey-200 bg-white px-3 text-sm focus-within:border-black',
					isOpen && 'border-black'
				)}
				onClick={() => setIsOpen(!isOpen)}
				tabIndex={0}
				role="combobox"
				aria-expanded={isOpen}
				aria-haspopup="listbox"
				aria-controls="options-list"
			>
				{selected.length === 0 ? (
					<span className="text-gray-500">{placeholder}</span>
				) : (
					selected.map((val, index) => (
						<span
							key={index}
							className="inline-flex items-center rounded-md bg-secondary px-2 py-1 text-xs font-medium text-secondary-foreground"
						>
							{getSelectedLabel(val)}
							<button
								aria-label={`Remove ${getSelectedLabel(val)}`}
								className="ml-1 rounded-full text-secondary-foreground hover:bg-secondary-foreground/20 focus:outline-none focus:ring-2 focus:ring-ring"
								disabled={disabledOptions.includes(val)}
								onClick={e => removeOption(selected[index], e)}
								type="button"
							>
								<Cross aria-hidden className="h-3 w-3 text-gray-500" />
							</button>
						</span>
					))
				)}
				<ChevronDown className="ml-auto mr-1 h-3 w-3 text-gray-500" />
			</div>

			{isOpen && (
				<div className="border-input absolute z-10 mt-1 w-full rounded-md border bg-white shadow-md">
					{searchable && (
						<div className="p-2">
							<input
								ref={searchInputRef}
								type="text"
								className="border-input flex h-10 w-full rounded-md border bg-transparent px-3 py-2 text-sm placeholder:text-grey-500 focus:outline-none focus:ring-1 focus:ring-ring"
								placeholder={placeholder || t('common.select')}
								value={searchTerm}
								onChange={e => setSearchTerm(e.target.value)}
								onClick={e => e.stopPropagation()}
							/>
						</div>
					)}
					<ul
						className="max-h-60 overflow-auto p-1"
						role="listbox"
						id="options-list"
						aria-multiselectable="true"
					>
						{filteredOptions.length === 0 ? (
							<li className="px-2 py-2 text-center text-sm text-grey-500">
								{t('common.noOptions')}
							</li>
						) : (
							filteredOptions.map(option => {
								const isSelected = selected.includes(option.value);
								const isDisabled = disabledOptions.includes(
									option.value
								);

								return (
									<li
										key={option.value}
										className={`hover:bg-accent hover:text-accent-foreground relative flex cursor-default cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none ${
											isSelected ? 'bg-grey-100' : ''
										} ${isDisabled ? 'pointer-events-none opacity-50' : ''} `}
										onClick={
											isDisabled
												? undefined
												: () => toggleOption(option.value)
										}
										role="option"
										aria-disabled={isDisabled}
										aria-selected={isSelected}
										tabIndex={-1}
									>
										<div
											className={`mr-2 flex h-4 w-4 items-center justify-center rounded-sm border ${
												isSelected
													? 'border-primary bg-primary text-primary-foreground'
													: 'border-primary opacity-50'
											}`}
										>
											{isSelected && (
												<Check
													aria-hidden
													className="h-4 w-4 text-grey-100"
												/>
											)}
										</div>
										<span>{option.label}</span>
									</li>
								);
							})
						)}
					</ul>
				</div>
			)}
		</div>
	);
}
